import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Chip,
    Checkbox
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const PendingChargesList = ({ plateNo, weekKey, onSelectionChange }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [charges, setCharges] = useState([]);
    const [selectedCharges, setSelectedCharges] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (plateNo) {
            fetchPendingCharges();
        }
    }, [plateNo, weekKey]);

    const fetchPendingCharges = async () => {
        setLoading(true);
        try {
            const params = { plate_no: plateNo };
            if (weekKey) params.week_key = weekKey;

            const response = await axios.get(
                `${rootUrl}/api/remittance/pending-charges`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                    params
                }
            );

            if (response.data && response.data.success) {
                setCharges(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching pending charges:', error);
            setCharges([]);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleCharge = (charge) => {
        const isSelected = selectedCharges.some(
            c => c.charge_reference_id === charge.charge_reference_id && c.charge_type === charge.charge_type
        );

        let newSelection;
        if (isSelected) {
            newSelection = selectedCharges.filter(
                c => !(c.charge_reference_id === charge.charge_reference_id && c.charge_type === charge.charge_type)
            );
        } else {
            newSelection = [...selectedCharges, charge];
        }

        setSelectedCharges(newSelection);
        onSelectionChange && onSelectionChange(newSelection);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    const getChargeTypeColor = (type) => {
        const colors = {
            'JOB_CARD': 'blue',
            'EXPENSE': 'orange',
            'PART_PURCHASE': 'purple'
        };
        return colors[type] || 'gray';
    };

    if (loading) {
        return (
            <Card>
                <CardBody className="text-center py-8">
                    <Typography>Loading pending charges...</Typography>
                </CardBody>
            </Card>
        );
    }

    if (charges.length === 0) {
        return (
            <Card>
                <CardBody className="text-center py-8">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400 mb-2" />
                    <Typography variant="paragraph" className="text-gray-600">
                        No pending charges found
                    </Typography>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            <CardBody className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-4 border-b border-gray-300">Select</th>
                                <th className="p-4 border-b border-gray-300">Type</th>
                                <th className="p-4 border-b border-gray-300">Description</th>
                                <th className="p-4 border-b border-gray-300">Date</th>
                                <th className="p-4 border-b border-gray-300">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charges.map((charge, index) => {
                                const isSelected = selectedCharges.some(
                                    c => c.charge_reference_id === charge.charge_reference_id && c.charge_type === charge.charge_type
                                );

                                return (
                                    <tr
                                        key={`${charge.charge_type}-${charge.charge_reference_id}-${index}`}
                                        className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                                    >
                                        <td className="p-4 border-b border-gray-200">
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => handleToggleCharge(charge)}
                                            />
                                        </td>
                                        <td className="p-4 border-b border-gray-200">
                                            <Chip
                                                value={charge.charge_type}
                                                color={getChargeTypeColor(charge.charge_type)}
                                                size="sm"
                                            />
                                        </td>
                                        <td className="p-4 border-b border-gray-200">
                                            <Typography variant="small">
                                                {charge.description || charge.charge_reference_id}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-gray-200">
                                            <Typography variant="small">
                                                {formatDate(charge.opened_date)}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-gray-200">
                                            <Typography variant="small" className="font-semibold">
                                                {formatCurrency(charge.charge_amount)}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </CardBody>
        </Card>
    );
};

export default PendingChargesList;

